import type { AxiosRequestConfig } from 'axios'
import axios from 'axios'
import { storeToRefs } from 'pinia'

import { useAuth0 } from '@auth0/auth0-vue'
import * as Sentry from '@sentry/vue'

import type { IUseLogout } from '~/portal/composables/useLogout.ts'
import { useLogout } from '~/portal/composables/useLogout.ts'
import { useStateStore } from '~/portal/stores'
import type { STATE_STORE_KEYS } from '~/portal/types'

const httpClient = axios.create({
	baseURL: import.meta.env.VITE_API_URL,
	timeout: 1200000,
	headers: {
		'Content-Type': 'application/json'
	},
	paramsSerializer: {
		indexes: null
	}
})

const setupInterceptors = () => {
	const { getAccessTokenSilently } = useAuth0()
	const { startLogout }: IUseLogout = useLogout()

	httpClient.interceptors.request.use(
		async config => {
			if ((config as any).skipAuth) {
				delete (config as any).skipAuth
				return config
			}

			try {
				const token = await getAccessTokenSilently()

				if (token) {
					config.headers.Authorization = `Bearer ${token}`
				}

				return config
			} catch (error) {
				startLogout()
				return Promise.reject(error)
			}
		},
		function (error: Error) {
			return Promise.reject(error)
		}
	)

	httpClient.interceptors.response.use(
		response => {
			if (response.status === 204 || response.status === 201) {
				return response
			}
			return response
		},
		error => {
			if (error.response?.status === 401) {
				startLogout()
				return
			}

			return Promise.reject(error)
		}
	)
}

enum FETCH_API_METHODS {
	Get = 'GET',
	Post = 'POST',
	Put = 'PUT',
	Delete = 'DELETE',
	Patch = 'PATCH'
}

export interface IFetchApiProps {
	url: string
	method: FETCH_API_METHODS
	pendingKey: STATE_STORE_KEYS
	payload?: any
	params?: any
	forceFetch?: boolean
	replacePending?: boolean
	timeout?: number
	token?: string
	responseType?: 'json' | 'blob' | 'text'
	headers?: Record<string, string>
	skipAuth?: boolean
}

const abortControllers: Partial<Record<STATE_STORE_KEYS, AbortController>> = {}
const replacedControllers = new WeakSet<AbortController>()

const fetchApiAbort = (pendingKey: STATE_STORE_KEYS) => {
	abortControllers[pendingKey]?.abort()
	delete abortControllers[pendingKey]
}

const fetchApi = async ({
	url,
	method,
	pendingKey,
	payload,
	params,
	forceFetch,
	replacePending,
	timeout,
	responseType,
	headers,
	skipAuth
}: IFetchApiProps) => {
	const stateStore = useStateStore()
	const { loading } = storeToRefs(stateStore)

	if (loading.value[pendingKey] && !forceFetch && !replacePending) {
		return
	}

	const pendingController = abortControllers[pendingKey]
	if (replacePending && pendingController) {
		replacedControllers.add(pendingController)
		pendingController.abort()
	}

	const controller = new AbortController()
	abortControllers[pendingKey] = controller

	try {
		if (!forceFetch) {
			stateStore.updateLoadingStatus(true, pendingKey)
		}

		const options: AxiosRequestConfig = {
			params,
			headers,
			signal: controller.signal
		}

		;(options as any).skipAuth = skipAuth

		if (timeout) {
			options.timeout = timeout
		}

		if (responseType) options.responseType = responseType

		switch (method) {
			case FETCH_API_METHODS.Get:
				return await httpClient.get(url, options)
			case FETCH_API_METHODS.Post:
				return await httpClient.post(url, payload, options)
			case FETCH_API_METHODS.Put:
				return await httpClient.put(url, payload, options)
			case FETCH_API_METHODS.Delete:
				return await httpClient.delete(url, { ...options, data: payload })
			case FETCH_API_METHODS.Patch:
				return await httpClient.patch(url, payload, options)
			default:
				throw new Error(`Unsupported HTTP method: ${method}`)
		}
	} catch (error) {
		if (axios.isCancel(error)) {
			return
		}

		stateStore.updateErrorStatus(error, pendingKey)
		Sentry.captureException(error)
		throw new Error(JSON.stringify(error))
	} finally {
		if (abortControllers[pendingKey] === controller) {
			delete abortControllers[pendingKey]
		}

		if (!forceFetch && !replacedControllers.has(controller)) {
			stateStore.updateLoadingStatus(false, pendingKey)
		}
	}
}

export {
	FETCH_API_METHODS,
	fetchApi,
	fetchApiAbort,
	httpClient,
	setupInterceptors
}
